import type { Task } from "~/core/api/index";
import { readingPath } from "~/modules/analytics/lib/loop";

export const TASK_KINDS = [
	"sample.batch",
	"samples.collect",
	"questions.generate",
	"claims.extract",
	"generate.page",
	"opportunities.report",
	"publish.page",
	"reflow.page",
	"reflow.compare",
] as const;
export const TASK_STATUSES = ["running", "queued", "failed", "done", "skipped"] as const;

/**
 * The kinds a *running* task can be asked to stop in the middle of.
 *
 * A copy of `pausable_while_running` in `server/src/loops/jobs.rs`, kept here
 * because the button has to be drawn before any answer comes back, and kept
 * honest by `tasks.test.ts`, which reads the Rust table. A kind the server
 * would refuse is a button that fails in front of a customer.
 */
const PAUSABLE_WHILE_RUNNING: string[] = ["sample.batch", "samples.collect", "claims.extract", "generate.page"];

/**
 * Pausing a queued task is nothing but not starting it, so every kind allows
 * it; stopping one mid-run only makes sense where the next run skips what this
 * one already bought. Publishing and the comparisons are the other shape —
 * seconds long, no model call, no middle to stop in.
 */
export function canPause(task: Task): boolean {
	if (task.paused_at) return false;
	return task.status === "queued" || (task.status === "running" && PAUSABLE_WHILE_RUNNING.includes(task.kind));
}

/** A paused task is queued (or still finishing its current step) and waiting for somebody. */
export function canResume(task: Task): boolean {
	return Boolean(task.paused_at) && (task.status === "queued" || task.status === "running");
}

/**
 * A task that ended badly can be run again from the start.
 *
 * Failed only, not skipped: a skipped task is a gate that was closed, and
 * pressing it again before the gate opens buys nothing. Mirrors the status
 * guard in `jobs::retry`.
 */
export function canRetry(task: Task): boolean {
	return task.status === "failed";
}

export function taskTarget(task: Task): string | null {
	if (!task.entity_id || task.status !== "done") return null;
	const base = `/w/${task.entity_id}`;
	if (task.page_id) return `${base}/pages/${task.page_id}`;
	switch (task.kind) {
		case "sample.batch":
		case "samples.collect":
			return readingPath(base, "visibility");
		case "questions.generate":
			return readingPath(base, "prompts");
		case "claims.extract":
			return readingPath(base, "factcheck");
		case "opportunities.report":
			return readingPath(base, "opportunities");
		case "reflow.compare":
			return readingPath(base, "reflows");
		default:
			return null;
	}
}
